import {
  type Signal,
  LOGICAL_OPERATORS,
  type ValueTypeToAvailableUnits,
  type ReqsReviewConfigItem,
  type FilterUnits,
  OPERATORS,
  type FilterQuery,
  type FilterGroup,
  REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES,
  FILTER_QUERY_VALUE_TYPES,
  CountQuery,
} from '@frontend/api';

import { uuid } from 'utils/stringUtils';

import {
  isHelperKey,
  HELPER_QUERY_MAP,
  isCountHelperSubmenuKey,
  isCountHelperKey,
} from './cascader';
import { validateValueCompatibleWithValueType } from './validation';
import { validateValueCompatibleWithOperator } from './validation';

// Enhanced types with IDs
// NB: We want unique IDs for each query, group, and signal for unique React keys,
// not for any specific data reasons
export type QueryWithId = FilterQuery & { id: string };
export type GroupWithId = Omit<FilterGroup, 'queries'> & {
  id: string;
  queries: QueryWithId[];
};
export type SignalWithId = Omit<Signal, 'groups'> & {
  id: string;
  groups: GroupWithId[];
};

// Helper function to strip IDs before mutation
export const stripIdsFromSignal = (signal: SignalWithId): Signal => {
  const { groups, ...signalRest } = signal;
  return {
    ...signalRest,
    groups: groups.map(({ queries, ...groupRest }) => ({
      ...groupRest,
      queries: queries.map(({ id, ...queryRest }) => queryRest),
    })),
  };
};

const addIdsToQuery = (query: FilterQuery): QueryWithId => ({
  ...query,
  id: uuid(),
});

const addIdsToGroup = (group: FilterGroup): GroupWithId => ({
  ...group,
  id: uuid(),
  queries: group.queries.map(addIdsToQuery),
});

// Helper function to add IDs to existing signals
export const addIdsToSignal = (signal: Signal): SignalWithId => ({
  ...signal,
  id: uuid(),
  groups: signal.groups.map((group) => addIdsToGroup(group)),
});

const getInitialOperator = (configItem: ReqsReviewConfigItem) => {
  if (
    configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.SELECT &&
    configItem.allowMultiSelect
  ) {
    return OPERATORS.IS_IN;
  }
  if (configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.STRING) {
    return OPERATORS.INCLUDES_CASE_INSENSITIVE;
  }
  return OPERATORS.EQUALS;
};

const getInitialValue = (configItem: ReqsReviewConfigItem) => {
  if (
    configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.SELECT &&
    configItem.allowMultiSelect
  ) {
    return [];
  } else if (
    configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.SELECT &&
    !configItem.allowMultiSelect
  ) {
    const selectOptions = configItem.selectOptions ?? [];
    return selectOptions.length > 0 ? selectOptions[0].value : '';
  } else if (
    configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.NUMERIC
  ) {
    return 0;
  } else {
    return '';
  }
};

export const createNewFilterQuery = (
  configItem: ReqsReviewConfigItem,
  valueTypeMap: ValueTypeToAvailableUnits,
  helperQuery?: Pick<FilterQuery, 'value' | 'operator'>,
) => {
  const availableUnits = configItem.valueType
    ? valueTypeMap[configItem.valueType]
    : [];
  const initialQuery = {
    filterType:
      configItem.filterType ?? REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.STRING,
    operator: getInitialOperator(configItem),
    valueType: configItem.valueType ?? FILTER_QUERY_VALUE_TYPES.STRING,
    units:
      availableUnits?.length > 0 ? (availableUnits[0] as FilterUnits) : null,
    value: getInitialValue(configItem),
    fieldName: configItem.queryPath ?? [],
    ...helperQuery,
  };

  return addIdsToQuery(initialQuery);
};

const getConfigFromCascaderValue = (
  nextCascaderValue: string[],
  configMap: Record<string, ReqsReviewConfigItem>,
) => {
  const lastValue = nextCascaderValue[nextCascaderValue.length - 1];
  const isHelper = isHelperKey(lastValue);
  const configPath = isHelper
    ? nextCascaderValue[nextCascaderValue.length - 2]
    : lastValue;
  return configMap[configPath];
};

/**
 * Build the next query based on a selection from the query building cascader.
 *
 * This selection will specify the next field and operator to be used for the query.
 * This function will then build the query based on the helper and defaults associated
 * with the configuration of the next field and operator. If the current query value is
 * compatible with the future query value, the current value will be used. Otherwise the
 * function will provide a default value.
 *
 * @param nextCascaderValue The next cascader value
 * @param configMap The config map
 * @param currentQuery The current query
 * @param valueTypeToAvailableUnits The value type to available units
 * @returns The next query
 */
export const buildQueryFromCascaderValue = ({
  nextCascaderValue,
  configMap,
  currentQuery,
  valueTypeToAvailableUnits,
}: {
  nextCascaderValue: string[];
  configMap: Record<string, ReqsReviewConfigItem>;
  currentQuery?: QueryWithId;
  valueTypeToAvailableUnits: ValueTypeToAvailableUnits;
}) => {
  const lastValue = nextCascaderValue[nextCascaderValue.length - 1];
  const isHelper = isHelperKey(lastValue);
  if (!isHelper) {
    // NOTE: now that cascader drives setting field and operator, every leaf option should
    // be a helper
    console.error(`Helper not found for cascader value ${nextCascaderValue}`);
    return;
  }

  const isCountHelper = isCountHelperKey(lastValue);
  // Here we want to check if the second to last value is a count submenu specifically
  const secondToLastValue = nextCascaderValue[nextCascaderValue.length - 2];
  const isCountSubmenu = isCountHelperSubmenuKey(secondToLastValue);

  // If it is, then we actually want to slice out the second to last value, so we can evaluate
  // the last value only as a helper
  const nextCascaderValueWithoutCountSubmenu = nextCascaderValue;

  const selectedConfig = getConfigFromCascaderValue(
    nextCascaderValueWithoutCountSubmenu,
    configMap,
  );
  if (!selectedConfig) {
    console.error(
      `Selected config not found for cascader value ${nextCascaderValue}`,
    );
    return;
  }

  // If we're in a count query helper, ignore the helper query
  const helperQuery =
    isCountSubmenu || isCountHelper ? undefined : HELPER_QUERY_MAP[lastValue];

  if (
    currentQuery &&
    helperQuery &&
    selectedConfig.valueType &&
    selectedConfig.filterType !== REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.BOOLEAN
  ) {
    // if the current value is compatible with the next operator, conserve the active
    // value, otherwise reset to the initial value
    const {
      isValid: isValidValueType,
      validatedValue: validatedValueFromValueTypeCheck,
    } = validateValueCompatibleWithValueType(
      currentQuery.value,
      selectedConfig.valueType,
    );
    if (isValidValueType && validatedValueFromValueTypeCheck) {
      const { isValid: isValidOperator } = validateValueCompatibleWithOperator(
        validatedValueFromValueTypeCheck,
        helperQuery.operator,
      );

      if (isValidOperator) {
        helperQuery.value = currentQuery.value;
      }
    }
  }

  // rebuilding the query will cover if its a new field or just a helper change
  const nextQuery = createNewFilterQuery(
    selectedConfig,
    valueTypeToAvailableUnits,
    helperQuery,
  );
  // keep the same id if query provided
  if (currentQuery) {
    return { ...nextQuery, id: currentQuery.id };
  } else {
    return nextQuery;
  }
};

export const buildFilterGroupFromCascaderValue = ({
  nextCascaderValue,
  configMap,
  valueTypeToAvailableUnits,
}: {
  nextCascaderValue: string[];
  configMap: Record<string, ReqsReviewConfigItem>;
  currentGroup?: GroupWithId;
  valueTypeToAvailableUnits: ValueTypeToAvailableUnits;
}) => {
  const lastValue = nextCascaderValue[nextCascaderValue.length - 1];
  const isCountHelper = isCountHelperKey(lastValue);
  // Here we want to check if the second to last value is a count submenu specifically
  const secondToLastValue = nextCascaderValue[nextCascaderValue.length - 2];
  const isFromCountHelperSubmenu = isCountHelperSubmenuKey(secondToLastValue);
  // if it is, then we actually want to slice out the second to last value, so we can evaluate
  // the last value only as a helper
  let nextCascaderValueWithoutCountSubmenu = nextCascaderValue;
  if (isFromCountHelperSubmenu) {
    nextCascaderValueWithoutCountSubmenu = [
      ...nextCascaderValue.slice(0, -2),
      lastValue,
    ];
  }

  let countQuery = null;
  if (isCountHelper || isFromCountHelperSubmenu) {
    countQuery = HELPER_QUERY_MAP[lastValue] as CountQuery;
  }

  const nextQuery =
    isCountHelper || isFromCountHelperSubmenu
      ? null
      : buildQueryFromCascaderValue({
          nextCascaderValue,
          configMap,
          valueTypeToAvailableUnits,
        });

  const initialQueries = nextQuery ? [nextQuery] : [];

  const selectedConfig = getConfigFromCascaderValue(
    nextCascaderValueWithoutCountSubmenu,
    configMap,
  );
  if (!selectedConfig) {
    console.error(
      `Selected config not found for cascader value ${nextCascaderValue}`,
    );
    return;
  }

  return addIdsToGroup({
    queries: initialQueries,
    documentPath: selectedConfig.parentDocumentPath,
    logicalOperator: LOGICAL_OPERATORS.AND,
    countQuery,
  });
};

export const buildSignalFromCascaderValue = ({
  nextCascaderValue,
  configMap,
  valueTypeToAvailableUnits,
}: {
  nextCascaderValue: string[];
  configMap: Record<string, ReqsReviewConfigItem>;
  valueTypeToAvailableUnits: ValueTypeToAvailableUnits;
}) => {
  const nextGroup = buildFilterGroupFromCascaderValue({
    nextCascaderValue,
    configMap,
    valueTypeToAvailableUnits,
  });
  if (!nextGroup) {
    console.error(
      `Next group not found for cascader value ${nextCascaderValue}`,
    );
    return;
  }

  return addIdsToSignal({
    groups: [nextGroup],
    logicalOperator: LOGICAL_OPERATORS.AND,
  });
};
